
import React, { useLayoutEffect, useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from './Layouts/Header';
import Footer from './Layouts/Footer';
import { getItems, addToCart } from '../utils/apiService';

const ProductListing = () => {
    const { slug } = useParams(); // Get category slug from URL
    const navigate = useNavigate();
    const [selectedPrice, setSelectedPrice] = useState(0);

    const [selectedBrands, setSelectedBrands] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryTitle, setCategoryTitle] = useState('Products');
    const [availableBrands, setAvailableBrands] = useState([]);
    const [addingToCart, setAddingToCart] = useState({});
    const [currency, setCurrency] = useState('$');

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await getItems(slug);
                if (response && response.status && response.data) {
                    setProducts(response.data);

                    // Extract category title from first product if available
                    // if (response.data.length > 0 && response.data[0].category_title) {
                    //     setCategoryTitle(response.data[0].category_title);
                    // }
                    if (response.data.length > 0) {
                        if (response.data[0].category_title) {
                            setCategoryTitle(response.data[0].category_title);
                        }
                        // Set currency from first product if available
                        if (response.data[0].currency) {
                            setCurrency(response.data[0].currency);
                        }
                    }

                    // Extract unique brands from products
                    const brands = [...new Set(response.data
                        .map(p => p.brand)
                        .filter(brand => brand && brand.trim() !== '')
                    )];
                    setAvailableBrands(brands);
                }
            } catch (error) {
                console.error('Error fetching products:', error);
            }
            setLoading(false);
        };

        if (slug) {
            fetchProducts();
        }
    }, [slug]);

    const minPrice = products.length
        ? Math.min(...products.map(p => Number(p.price)))
        : 0;

    const maxPrice = products.length
        ? Math.max(...products.map(p => Number(p.price)))
        : 0;

    useEffect(() => {
        if (products.length) {
            setSelectedPrice(maxPrice); // start full range
        }
    }, [products, maxPrice]);

    useLayoutEffect(() => {
        if (loading) return;

        const timer = setTimeout(() => {
            if (window.animationText) window.animationText();
            if (window.scrollingEffect) window.scrollingEffect();
            if (window.animateImgItem) window.animateImgItem();

            if (window.ScrollTrigger) {
                window.ScrollTrigger.refresh();
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (window.revertAnimations) window.revertAnimations();
            if (window.revertHeadline) window.revertHeadline();
        };
    }, [loading, products]);

    const handleBrandChange = (brand) => {
        setSelectedBrands(prev =>
            prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
        );
    };

    const getImageUrl = (imgVal) => {
        if (!imgVal) return '';
        const imgStr = imgVal.toString().trim();
        // If it's a full URL, data URI, or already an absolute path
        if (imgStr.startsWith('http') || imgStr.startsWith('data:') || imgStr.startsWith('/')) {
            return imgStr;
        }
        // Otherwise, assume it's a storage path from the backend
        return `${process.env.REACT_APP_STORAGE_BASE_URL || 'http://localhost:8000/storage'}/${imgStr}`;
    };

    const handleAddToCart = async (productId, e) => {
        e.preventDefault();

        // Check if user is logged in
        const token = localStorage.getItem('token');
        if (!token) {
            // Redirect to login page or show login modal
            toast.info('Please login to add items to cart');
            navigate('/login');
            return;
        }

        // Set loading state for this specific product
        setAddingToCart(prev => ({ ...prev, [productId]: true }));

        try {
            const response = await addToCart(
                {
                    itemId: productId,
                    quantity: 1
                },
                token
            );

            if (response && response.status) {
                // Success
                toast.success(response.message || 'Item added to cart successfully!');
                // Update cart count in header
                window.dispatchEvent(new CustomEvent('cartUpdated'));
            } else {
                // Handle error
                toast.error(response.message || 'Failed to add item to cart');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error('An error occurred while adding to cart');
        } finally {
            // Remove loading state
            setAddingToCart(prev => ({ ...prev, [productId]: false }));
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesBrand = selectedBrands.length === 0 || selectedBrands.includes(product.brand);
        const matchesPrice = Number(product.price) >= minPrice && Number(product.price) <= selectedPrice
        return matchesBrand && matchesPrice;
    });

    const getCurrency = (product) => {
        return product.currency || currency;
    };

    return (
        <div className="wrapper position-relative">
            <Header />

            <div className="page-title">
                <div className="tf-container">
                    <div className="page-title-content text-center">
                        <h1 className="title">
                            <span className="split-text effect-right">Products</span>
                        </h1>
                        <div className="breadkcum">
                            <Link to="/" className="link-breadkcum body-2 fw-7">
                                <span className="split-text effect-right">Home</span>
                            </Link>
                            <span className="dot"></span>
                            <Link to="/item" className="link-breadkcum body-2 fw-7">
                                <span className="split-text effect-right">Categories</span>
                            </Link>
                            <span className="dot"></span>
                            <span className="page-breadkcum body-2 fw-7">
                                <span className="split-text effect-right">{categoryTitle}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="main-content">
                <section className="section-team p-team tf-spacing-2">
                    <div className="tf-container">
                        {loading ? (
                            <div className="text-center py-5">Loading products...</div>
                        ) : (
                            <div className="row rg-30">
                                {/* Filters Sidebar */}
                                <div className="col-lg-3 col-md-12">
                                    <div className="filter-box mb-4">
                                        <h5>Filters</h5>
                                        {availableBrands.length > 0 && (
                                            <>
                                                <h6 className="title-content">Brand</h6>
                                                <div className="product-filter">
                                                    {availableBrands.map(brand => (
                                                        <div key={brand} className="form-check">
                                                            <input
                                                                className="filter-checkbox"
                                                                type="checkbox"
                                                                checked={selectedBrands.includes(brand)}
                                                                onChange={() => handleBrandChange(brand)}
                                                            /> {brand}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        <div className="price-filter mt-4">
                                            <h6 className="title-content">Price</h6>
                                            <input
                                                type="range"
                                                className="form-range mb-15"
                                                id="priceRange"
                                                min={minPrice}
                                                max={maxPrice}
                                                step="10"
                                                value={selectedPrice}
                                                onChange={(e) => setSelectedPrice(Number(e.target.value))}

                                            />
                                            <div className="d-flex justify-content-between">
                                                <span id="priceText">{currency}{minPrice.toLocaleString()} - {currency}{selectedPrice.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Product Listing */}
                                <div className="col-lg-9 col-md-12">
                                    <h4 className="fw-bold mb-3">{categoryTitle}</h4>
                                    <div className="row g-4">
                                        {filteredProducts.length > 0 ? (
                                            filteredProducts.map((product) => (
                                                <div key={product.id} className="col-lg-4 col-md-6">
                                                    <div className="product-card team-item product-listing product-item hover-image">
                                                        <div className="top-item">
                                                            <Link to={`/item-details/${product.id}`}>
                                                                <img
                                                                    src={getImageUrl(product.image)}
                                                                    alt={product.title}
                                                                    className="ls-is-cached lazyloaded"
                                                                />
                                                            </Link>
                                                        </div>
                                                        <div className="item-content">
                                                            <h6 className="title">{product.title}</h6>
                                                            <div className="price">{getCurrency(product)}{product.price.toLocaleString()}</div>
                                                            <button
                                                                onClick={(e) => handleAddToCart(product.id, e)}
                                                                disabled={addingToCart[product.id]}
                                                                className="tf-btn style-border w-full justify-content-center"
                                                            >
                                                                <span>
                                                                    {addingToCart[product.id]
                                                                        ? 'Adding...'
                                                                        : 'Add to Cart'
                                                                    }
                                                                </span>
                                                                <i className="icon-arrow-right"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center py-5">
                                                <p>No products match your criteria.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
};

export default ProductListing;